/**
 * Subgraph building functions for Local Map.
 *
 * These are pure functions that build subgraphs from graph data,
 * allowing the LocalViewController to delegate subgraph construction
 * while keeping the logic testable and independently improvable.
 *
 * @module subgraph-builder
 */

import type { LocalSubgraph, LocalSubgraphLink } from "./types";
import type { ExplorerNodePayload, ExplorerLinkPayload, ExplorerGraphPayload } from "../../../shared/types";

/**
 * Function to resolve a link endpoint to a node ID.
 */
export type LinkEndpointResolver = (endpoint: ExplorerLinkPayload["source"]) => string;

/**
 * Function to resolve a node by ID.
 */
export type NodeResolver = (id: string) => ExplorerNodePayload | undefined;

/**
 * Build self-loop edges from intra-file type references.
 *
 * When a symbol references another symbol in the same file, we create a self-loop edge,
 * which the connections module draws as the two laces of the French Corset.
 */
export function buildSelfLoopEdges(center: ExplorerNodePayload): LocalSubgraphLink[] {
  const selfLoopEdges: LocalSubgraphLink[] = [];
  const seenKeys = new Set<string>();

  const symbols = center.publicSymbolsExtended;
  if (!symbols) return selfLoopEdges;

  // Build a set of symbol names in this file for quick lookup
  const localSymbolNames = new Set(symbols.map(s => s.name));
  const namesByAnchor = new Map(symbols.filter(s => s.anchor).map(s => [s.anchor, s.name]));

  for (const symbol of symbols) {
    const typeRefs = symbol.typeReferences;
    if (!typeRefs) continue;

    for (const ref of typeRefs) {
      // Check if this type reference points to a symbol in the same file
      const isSelfReference =
        (ref.isResolved && ref.targetId === center.id) ||
        (!ref.isResolved && (namesByAnchor.has(ref.targetAnchor) || localSymbolNames.has(ref.typeName)));

      if (isSelfReference) {
        const targetSymbol = namesByAnchor.get(ref.targetAnchor) ?? ref.typeName;
        const key = `${center.id}|${center.id}|type-reference|${symbol.name}|${targetSymbol}`;
        if (seenKeys.has(key)) continue;
        seenKeys.add(key);

        selfLoopEdges.push({
          sourceId: center.id,
          targetId: center.id,
          direction: "outbound",
          kind: "type-reference",
          sourceSymbol: symbol.name,
          targetSymbol
        });
      }
    }
  }

  return selfLoopEdges;
}

/**
 * Builds a subgraph for path mode visualization.
 *
 * Unlike exploration mode which shows all neighbors of a center node,
 * path mode shows only the nodes in the path and edges between adjacent nodes.
 *
 * For a path [A, B, C]:
 * - A is the "origin" (FROM)
 * - C is the "destination" (TO)
 * - B is intermediate
 * - Edges are filtered to only include A→B and B→C connections
 *
 * A link's direction is relative to the earlier of its two files: "inbound"
 * when the later file depends on the earlier (the link runs with the path, and
 * the path drawer draws it), "outbound" when the earlier file depends on the
 * later (the link runs against the path and is counted, not drawn).
 */
export function buildPathSubgraph(
  pathNodeIds: string[],
  graphData: ExplorerGraphPayload,
  resolveLinkEndpoint: LinkEndpointResolver,
  resolveNode: NodeResolver
): LocalSubgraph | null {
  if (pathNodeIds.length < 2) return null;

  const nodes: ExplorerNodePayload[] = [];
  for (const id of pathNodeIds) {
    const node = resolveNode(id);
    if (!node) return null;
    nodes.push(node);
  }

  const center = nodes[0];
  const pathNodeIdSet = new Set(pathNodeIds);

  // Build set of valid adjacent pairs for edge filtering
  const adjacentPairs = new Set<string>();
  for (let i = 0; i < pathNodeIds.length - 1; i++) {
    adjacentPairs.add(`${pathNodeIds[i]}:${pathNodeIds[i + 1]}`);
    adjacentPairs.add(`${pathNodeIds[i + 1]}:${pathNodeIds[i]}`);
  }

  const linkResults: LocalSubgraphLink[] = [];
  const inboundIds = new Set<string>();
  const outboundIds = new Set<string>();

  graphData.links.forEach(edge => {
    const sourceId = resolveLinkEndpoint(edge.source);
    const targetId = resolveLinkEndpoint(edge.target);

    // Only include edges between adjacent path nodes
    const pairKey = `${sourceId}:${targetId}`;
    if (!adjacentPairs.has(pairKey)) return;

    // Both nodes must be in the path
    if (!pathNodeIdSet.has(sourceId) || !pathNodeIdSet.has(targetId)) return;

    const kind = edge.kind ?? "dependency";

    // Determine direction relative to the path flow
    const sourceIndex = pathNodeIds.indexOf(sourceId);
    const targetIndex = pathNodeIds.indexOf(targetId);
    const direction: "inbound" | "outbound" = sourceIndex < targetIndex ? "outbound" : "inbound";

    if (direction === "outbound") {
      outboundIds.add(targetId);
    } else {
      inboundIds.add(sourceId);
    }

    linkResults.push({
      sourceId,
      targetId,
      direction,
      kind,
      sourceSymbol: edge.sourceSymbol,
      targetSymbol: edge.targetSymbol,
      referenceCount: edge.referenceCount
    });
  });

  return {
    center,
    nodes,
    links: linkResults,
    inboundIds,
    outboundIds
  };
}
